"use client";

import Image from "next/image";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { FC, useMemo, useRef, useState } from "react";
import { z } from "zod";
// [RUDDERSTACK] widget_impression + cta_click are added centrally by NewPage
// (TrackedWidget + CtaClickDelegate). The CTA below carries data-cta-name so the
// delegate keeps the exact "view_more" name; no tracking code lives here.

/**
 * Course Outcome – Variant 2
 * "Where This Program Can Take You" – designed background-image cards (the
 * gradient/overlay is baked into the uploaded image per PRD; no CSS overlay)
 * with an optional title, paragraph and a two-tier "Avg Salary" highlight pill.
 *
 * Breakpoints (project, from globals.css):
 *   base = Mobile (<1080)   sm = 1080   md = 1230 (Figma 1280)
 *   lg   = 1430             xl = 1750 (Figma 1730)
 */

const POPPINS = "var(--font-poppins)";

// PRD: Min 3, Max 9 cards arranged as a 3×3 grid.
const MAX_CARDS = 9;

const MetadataSchema = z.object({
  id: z.union([z.number(), z.string()]),
  // PRD: bg image mandatory per card; gradient/overlay is baked into the image.
  meta_image: z.string().optional().default(""),
  meta_image_alt: z.string().optional().default(""),
  meta_mobile_image: z.string().optional().default(""),
  // Card title — optional, max 2 lines.
  meta_title: z.string().optional().default(""),
  meta_title_color: z.string().optional().default(""),
  // Card paragraph — optional, max 3 lines.
  meta_description: z.string().optional().default(""),
  meta_description_color: z.string().optional().default(""),
  // Card highlight (pill) — two-tier "Avg Salary" label + value, each 1 line
  // with its own colour, over a manual pill background colour.
  meta_box_color: z.string().optional().default(""),
  meta_box_title: z.string().optional().default(""),
  meta_box_title_color: z.string().optional().default(""),
  meta_box_sub_title: z.string().optional().default("Avg Salary"),
  meta_box_sub_title_color: z.string().optional().default(""),
  priority: z.coerce.number().optional().default(0),
  // Per-card show/hide toggle (defaults to visible for legacy rows).
  meta_status: z.union([z.boolean(), z.number()]).optional().default(true),
});

type Metadata = z.infer<typeof MetadataSchema>;

const CourseOutcome2DataSchema = z
  .object({
    desktop_background_color: z.string().optional().default(""),
    mobile_background_color: z.string().optional().default(""),
    // Heading color (reuses the existing `pre_title_text_color` DB column).
    pre_title_text_color: z.string().optional().default("#101112"),
    title: z.string().optional().default("Where This Program Can Take You"),
    description: z.string().optional().default(""),
    description_color: z.string().optional().default("#101112"),
    read_more_text: z.string().optional().default("View More"),
    read_more_color: z.string().optional().default(""),
    load_more_button: z.string().nullable().optional(),
    load_more_button_text_color: z.string().nullable().optional(),
    metadata: z.array(MetadataSchema).optional().default([]),
    variant: z.union([z.string(), z.number()]).optional().default(2),
    enable_animation: z
      .union([z.boolean(), z.number()])
      .optional()
      .default(false),
  })
  .transform((data) => ({
    ...data,
    metadata: data.metadata?.sort(
      (a, b) => (a.priority || 0) - (b.priority || 0),
    ),
  }));

// ── Career image card ──
// PRD: the bg image is mandatory and the gradient/overlay is baked into the
// uploaded image — we do NOT apply any CSS gradient or blur over it. Only the
// optional title/paragraph/highlight text sit on top.
const CareerCard: FC<{ role: Metadata; mobile: boolean }> = ({
  role,
  mobile,
}) => {
  const imageUrl = (mobile && role.meta_mobile_image) || role.meta_image || "";

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden bg-[#101112] shrink-0",
        "h-[320px] sm:h-[360px] md:h-[380px] lg:h-[418px] xl:h-[440px]",
        "rounded-[24px] sm:rounded-[30px]",
      )}
    >
      {/* Background image (next/image: WebP/AVIF + responsive srcset, lazy).
          Gradient/overlay is baked into this image per PRD. */}
      {imageUrl && (
        <Image
          src={imageUrl}
          alt={role.meta_image_alt || role.meta_title || ""}
          fill
          sizes="(min-width: 1080px) 33vw, 100vw"
          className="object-cover"
        />
      )}

      {/* Content anchored to the bottom */}
      <div className="absolute inset-0 flex flex-col justify-end">
        {/* Text block — title (max 2 lines) + paragraph (max 3 lines) */}
        {(role.meta_title || role.meta_description) && (
          <div
            className={cn(
              "relative flex flex-col gap-[8px]",
              "px-[12px] md:px-[16px] lg:px-[20px] xl:px-[40px]",
              "pb-[12px]",
            )}
          >
            {role.meta_title && (
              <p
                className={cn(
                  "line-clamp-2 font-semibold leading-[1.4] text-white",
                  "text-[16px] sm:text-[18px] md:text-[20px] lg:text-[22px] xl:text-[24px]",
                  "tracking-[-0.08px] sm:tracking-[-0.09px] md:tracking-[-0.1px] lg:tracking-[-0.11px] xl:tracking-[-0.12px]",
                )}
                style={{
                  fontFamily: POPPINS,
                  color: role.meta_title_color || "#FFFFFF",
                }}
              >
                {role.meta_title}
              </p>
            )}

            {role.meta_description && (
              <p
                className={cn(
                  "line-clamp-3",
                  "text-[14px] lg:text-[15px]",
                  "font-medium leading-[1.65] sm:font-normal sm:leading-[1.55]",
                )}
                style={{
                  fontFamily: POPPINS,
                  color: role.meta_description_color || "rgba(255,255,255,0.6)",
                }}
              >
                {role.meta_description}
              </p>
            )}
          </div>
        )}

        {/* Highlight pill — two-tier: muted "Avg Salary" label (body-sm) + bold
            value (h4). Each stays on one line. Manual pill background colour. */}
        {role.meta_box_title && (
          <div className="flex w-full justify-center">
            <div
              className={cn(
                "flex items-center justify-center gap-[10px] whitespace-nowrap",
                "rounded-tl-[24px] rounded-tr-[24px]",
                // Frosted-glass pill: top-lit hairline stroke (brighter on top,
                // fading down the sides) + subtle inner top sheen + blur.
                "border border-b-0 border-white/10 border-t-white/30 backdrop-blur-[8px]",
                "px-[30px] py-[14px] sm:py-[10px] lg:py-[14px]",
              )}
              style={{
                backgroundColor: role.meta_box_color || "rgba(0,0,0,0.2)",
                boxShadow: "inset 0 1px 0 0 rgba(255,255,255,0.2)",
              }}
            >
              {role.meta_box_sub_title && (
                <p
                  className={cn(
                    "leading-[1.55] tracking-[0.075px]",
                    "text-[14px] lg:text-[15px] xl:text-[16px]",
                  )}
                  style={{
                    fontFamily: POPPINS,
                    color: role.meta_box_sub_title_color || "rgba(255,255,255,0.6)",
                  }}
                >
                  {role.meta_box_sub_title}
                </p>
              )}
              <p
                className={cn(
                  "text-center leading-[1.3]",
                  "font-medium lg:font-semibold",
                  "text-[24px] lg:text-[28px] xl:text-[32px]",
                  "tracking-[-0.24px] lg:tracking-[-0.28px] xl:tracking-[-0.32px]",
                )}
                style={{
                  fontFamily: POPPINS,
                  color: role.meta_box_title_color || "#FFFFFF",
                }}
              >
                {role.meta_box_title}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const CourseOutcome2: FC<any> = (props) => {
  const actualData = props?.data?.data || props?.data || props;

  const isMobile = useIsMobile();
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const parsedResult = useMemo(
    () => CourseOutcome2DataSchema.safeParse(actualData),
    [actualData],
  );

  const ref = useRef<HTMLElement>(null);

  if (!parsedResult.success) {
    console.error("CourseOutcome2 validation failed", parsedResult.error);
    return null;
  }

  const parsedData = parsedResult.data;

  const isAnimationEnabled =
    parsedData.enable_animation === true || parsedData.enable_animation === 1;

  // Hide cards toggled off in the CMS (legacy rows with no flag stay visible).
  // PRD: max 9 cards (3×3 grid) — cap defensively even if more are configured.
  const cards = (parsedData.metadata ?? [])
    .filter((item) => item?.meta_status !== false && item?.meta_status !== 0)
    .slice(0, MAX_CARDS);

  // Mobile: show 3 cards until "View More" is clicked.
  const mobileCards = !mobileExpanded ? cards.slice(0, 3) : cards;
  const showViewMore = cards.length > 3 && !mobileExpanded;
  const viewMoreLabel =
    parsedData.load_more_button ?? parsedData.read_more_text ?? "View More";
  const viewMoreColor =
    parsedData.load_more_button_text_color ??
    parsedData.read_more_color ??
    "#101112";

  // PRD: a single mandatory Heading.
  const heading = (parsedData.title || "").trim();

  const bgColor = formatBackgroundStyle(parsedData, isMobile || false, "#FFFFFF");

  return (
    <section
      ref={ref}
      className={cn(
        "relative flex w-full justify-center overflow-hidden",
        "px-[24px] py-[60px]",
        "sm:p-[40px]",
        "md:px-[60px] md:py-[40px]",
        "lg:px-[80px] lg:py-[60px]",
        "xl:px-[160px] xl:py-[80px]",
      )}
      style={{ ...bgColor, fontFamily: POPPINS }}
    >
      <div
        className={cn(
          "flex w-full flex-col items-center",
          "gap-[22px] sm:gap-[40px] md:gap-[60px] lg:gap-[80px]",
        )}
      >
        {/* Header */}
        <div
          className={cn(
            "flex w-full flex-col items-center text-center",
            "gap-[22px] sm:gap-[18px] lg:gap-[22px]",
          )}
        >
          {heading && (
            <h2
              className={cn(
                "line-clamp-none sm:line-clamp-2 w-full font-semibold leading-[1.25]",
                "text-[28px] md:text-[32px] lg:text-[36px]",
                "tracking-[-0.42px] md:tracking-[-0.48px] lg:tracking-[-0.54px]",
              )}
              style={{ fontFamily: POPPINS, color: parsedData.pre_title_text_color || "#101112" }}
            >
              {heading}
            </h2>
          )}

          {parsedData.description && (
            <p
              className={cn(
                "line-clamp-none sm:line-clamp-3 w-full leading-[1.65]",
                "text-[14px] sm:text-[16px] lg:text-[17px]",
                "font-medium sm:font-normal lg:font-medium",
              )}
              style={{
                fontFamily: POPPINS,
                color: parsedData.description_color || "#101112",
              }}
            >
              {parsedData.description}
            </p>
          )}
        </div>

        {/* Cards */}
        {cards.length > 0 && (
          <div className="flex w-full flex-col items-center gap-[22px]">
            {/* Mobile – single column with "View More" */}
            <div className="grid w-full grid-cols-1 gap-[22px] sm:hidden">
              {mobileCards.map((role) => (
                <RevealOnScroll key={role.id} enableAnimation={isAnimationEnabled}>
                  <CareerCard role={role} mobile />
                </RevealOnScroll>
              ))}
            </div>

            {showViewMore && (
              <button
                type="button"
                data-cta-name="view_more"
                onClick={() => {
                  setMobileExpanded(true);
                }}
                className={cn(
                  "flex items-center justify-center font-semibold underline underline-offset-2 sm:hidden",
                  "text-[16px] leading-[1.4]",
                )}
                style={{
                  fontFamily: POPPINS,
                  color: viewMoreColor,
                  textDecorationColor: viewMoreColor,
                }}
              >
                {viewMoreLabel}
              </button>
            )}

            {/* Desktop – 3 columns */}
            <div
              className={cn(
                "hidden w-full sm:grid sm:grid-cols-3",
                "gap-[20px] xl:gap-[40px]",
              )}
            >
              {cards.map((role, indx) => (
                <RevealOnScroll
                  key={role.id}
                  y={-50}
                  scaleStart={0.85}
                  x={indx % 3 === 0 ? -120 : indx % 3 === 2 ? 120 : 0}
                  enableAnimation={isAnimationEnabled}
                >
                  <CareerCard role={role} mobile={false} />
                </RevealOnScroll>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
